<template>
  <div class="filter-card mb-3">
  <Form @submit="handleSubmit" ref="leaveReportFilterForm" :validation-schema="schema" v-slot="{ errors }">
    <div class="row align-items-end">

      <!-- Division -->
      <CustomSelect2 v-if="showDivision" class-name="col-md-4" label="Division" name="division_id" option-label="label"
        :options="divisions" v-model="selectedDivision" :loading="loadingDivision" :read-only="loadingDivision"
        @dropdown-value-change="handleDivisionChange" @remove="handleDivisionRemove" :not-required="true" />

      <!-- Department -->
      <CustomSelect2 v-if="showDept" class-name="col-md-4" label="Department" name="department_id" option-label="label"
        :options="departments" v-model="selectedDept" :loading="loadingDept" :read-only="loadingDept"
        @dropdown-value-change="handleDeptChange" @remove="handleDeptRemove" :not-required="true" />

      <!-- Designation -->
      <CustomSelect2 class-name="col-md-4" label="Designation" name="designation_id" option-label="label"
        :options="designations" v-model="selectedDesignation" :loading="loadingDesignation" :read-only="loadingDesignation"
        @dropdown-value-change="handleDesignationChange" @remove="handleDesignationChange" :not-required="true" />

      <!-- Employee -->
      <CustomSelect2 v-if="!multipleEmployees" class-name="col-md-4" label="Employee" name="employee_personal_id" option-label="label"
        :options="employees" v-model="selectedEmployee" :loading="loadingEmployees" :read-only="loadingEmployees"
        :error="!isEmployeeRequired ? errors.employee_personal_id : false" :not-required="isEmployeeRequired"
         />

         <div class="col-md-4" v-else>
                <div class="input-block mb-3">
                  <label class="col-form-label"
                    >Employee<span v-if="isEmployeeRequired" class="text-danger"></span
                  ></label>
                  <Field
                    v-slot="{ field }"
                    name="employee_personal_id"
                    :class="{ 'is-invalid': errors.employee_personal_id }"
                    class="form-control"
                  >
                    <multiselect
                      v-bind="field"
                      v-model="selectedEmployee"
                      :options="employees"
                      label="label"
                      track-by="value"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="true"
                      :searchable="true"
                      placeholder="Select Employee"
                      :disabled="loadingEmployees"
                      :loading="loadingEmployees"
                      :class="{ 'is-invalid': errors.employee_personal_id }"
                    >
                    </multiselect>
                  </Field>
                  <div v-if="isEmployeeRequired" class="invalid-feedback">
                    {{ errors.employee_personal_id }}
                  </div>
                  <div class="emailshow text-danger"></div>
                </div>
              </div>

              <!-- From -->
      <div class="col-md-4">
        <div class="input-block mb-3">
          <label class="col-form-label">From</label>
          <Field v-model="localValues.startDate" name="from" type="date" class="form-control" @change="resetEndDate"
            :disabled="!!localValues.month" :class="{ 'is-invalid': errors.from }" />
          <div class="invalid-feedback">{{ errors.from }}</div>
        </div>
      </div>

      <!-- To -->
      <div class="col-md-4">
        <div class="input-block mb-3">
          <label class="col-form-label">To</label>
          <Field v-model="localValues.endDate" name="to" type="date" class="form-control"
            @change="() => { localValues.month = ''; }"
            :disabled="!!localValues.month" :class="{ 'is-invalid': errors.to }" />
          <div class="invalid-feedback">{{ errors.to }}</div>
        </div>
      </div>

    </div>

    <div class="row align-items-end">
      <!-- Month -->
      <div class="col-md-4">
        <div class="input-block mb-3">
          <label class="col-form-label">Month</label>
          <Field v-model="localValues.month" name="month" type="month" class="form-control" @change="resetDateRange"
            :disabled="!!(localValues.startDate || localValues.endDate)" :class="{ 'is-invalid': errors.month }" />
          <small class="text-muted">Use either the Month filter or the From & To date filters, not both.</small>
          <div class="invalid-feedback">{{ errors.month }}</div>
        </div>
      </div>

      
      <!-- Search -->
      <div class="col-md-8 search-btn-col">
        <button type="button" class="btn btn-light" @click="resetFilters">Reset</button>
        <button class="btn btn-info">Search</button>
      </div>
    </div>
  </Form>
  </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import EmployeeService from "@/services/EmployeeService";
import DivisionService from "@/services/DivisionService";
import CustomSelect2 from "./CustomSelect2.vue";
import DepartmentService from "@/services/DepartmentService";
import DesignationService from "@/services/DesignationService";
import * as Yup from "yup";
import { computed } from "vue";
import Multiselect from "@/components/common/SortedMultiselect.vue";

export default {
  name: "LeaveFilterForm",
  components: {
    Form,
    Field,
    CustomSelect2,
    Multiselect,
  },

  props: {
    initialValues: {
      type: Object,
      default: () => ({
        startDate: "",
        endDate: "",
        month: "",
      })
    },
    showDivision: {
      type: Boolean,
      default: true,
    },
    showDept: {
      type: Boolean,
      default: true,
    },
    multipleEmployees: {
      type: Boolean,
      default: false,
    },
    isEmployeeRequired: {
      type: Boolean,
      default: false,
    },
    useEmployeeFilterApi: {
      type: Boolean,
      default: false,
    }
  },

  emits: ["submit", "reset"],

  data() {
    return {
      localValues: { month: "", ...this.initialValues },
      employees: [],
      selectedEmployee: null,
      loadingEmployees: false,
      divisions: [],
      selectedDivision: null,
      loadingDivision: false,
      departments: [],
      selectedDept: null,
      loadingDept: false,
      designations: [],
      selectedDesignation: null,
      loadingDesignation: false,
    };
  },

  methods: {
    resetEndDate() {
      this.localValues.endDate = "";
      this.localValues.month = "";
    },
    resetDateRange() {
      this.localValues.startDate = "";
      this.localValues.endDate = "";
    },
    async fetchEmployees() {
      this.loadingEmployees = true;
      try {
        const res = await EmployeeService.getAllEmployees();
        this.employees = res?.data?.map(el => ({
          value: el.id,
          label: `${el.employee_name}`,
        }));
      } finally {
        this.loadingEmployees = false;
      }
    },
    async fetchDivisions() {
      this.loadingDivision = true;
      try {
        const responseDivions = await DivisionService.getDivisions();
        this.divisions = responseDivions.data.map((el) => {
          return {
            value: el.id,
            label: el.name,
          };
        });
      } catch (error) {
        console.log('error fetching divisions', error);
      } finally {
        this.loadingDivision = false;
      }
    },
    async fetchDepartments() {
      this.loadingDept = true;
      try {
        const response = await DepartmentService.getDepartments();
        this.departments = response.data.map((el) => {
          return {
            value: el.id,
            label: el.name,
          };
        });
      } catch (error) {
        console.log('error fetching departments', error);
      } finally {
        this.loadingDept = false;
      }
    },
    async fetchDesignations() {
      this.loadingDesignation = true;
      try {
        const response = await DesignationService.getDesignations();
        this.designations = response.data.map((el) => ({
          value: el.id,
          label: el.designation_name,
        }));
      } catch (error) {
        console.log('error fetching designations', error);
      } finally {
        this.loadingDesignation = false;
      }
    },
    async fetchFilteredEmployees() {
      this.selectedEmployee = this.multipleEmployees ? [] : null;
      this.loadingEmployees = true;
      try {
        const params = {
          division_id: this.selectedDivision?.value || null,
          department_id: this.selectedDept?.value || null,
          designation_id: this.selectedDesignation?.value || null,
        };
        const res = await EmployeeService.getEmployeesByFilter(params);
        this.employees = res?.data?.map(el => ({
          value: el.id,
          label: el.employee_name,
        })) || [];
      } catch (error) {
        console.log('error fetching filtered employees', error);
      } finally {
        this.loadingEmployees = false;
      }
    },
    async handleDesignationChange() {
      if (this.useEmployeeFilterApi) {
        this.fetchFilteredEmployees();
      }
    },
    async handleDivisionChange() {
      try {
        if (this.useEmployeeFilterApi) {
          this.loadingDept = true;
          this.selectedDept = [];
          const divisionId = this.selectedDivision?.value;
          const response = await DepartmentService.getDepartmentsDivisionWise(
              divisionId
            );
            this.departments = response.data.map((el) => {
              return {
                value: el.id,
                label: el.name,
              };
            });
  
          return this.fetchFilteredEmployees();
        }
        this.loadingDept = true;
        this.loadingEmployees = true;
        this.selectedDept = [];
        this.selectedEmployee = [];
        const divisionId = this.selectedDivision?.value;
        if (this.showDept) {
          const response = await DepartmentService.getDepartmentsDivisionWise(
            divisionId
          );
          this.departments = response.data.map((el) => {
            return {
              value: el.id,
              label: el.name,
            };
          });
        }
        const payload = {
          division_id: divisionId,
        }
        const responseEmp = await EmployeeService.fetchDivisionWiseEmployee(
          payload,
        );
        this.employees = responseEmp.data.map((el) => {
          return {
            value: el.id,
            label: el.name,
          };
        });
      } catch (error) {
        this.departments = [];
        console.log(
          "response error from EmployeeService.getDepartmentEmployees",
          error
        );
      } finally {
        this.loadingDept = false;
        this.loadingEmployees = false;
      }
    },
    async handleDivisionRemove() {
      if (this.useEmployeeFilterApi) {
        this.fetchDepartments();
        return this.fetchFilteredEmployees();
      }
      this.fetchDepartments();
      this.fetchEmployees();
    },
    async handleDeptChange() {
      if (this.useEmployeeFilterApi) {
        return this.fetchFilteredEmployees();
      }
      this.loadingEmployees = true;
      this.selectedEmployee = [];
      try {
        const departmentId = this.selectedDept?.value;
        const responseEmp = await EmployeeService.getDivisionDepartmentEmployees(
          this.selectedDivision.value,
          departmentId,
        );
        this.employees = responseEmp.data.map((el) => {
          return {
            value: el.id,
            label: el.employee_name,
          };
        });
      } catch (error) {
        this.employees = [];
        console.log(
          "response error from EmployeeService.getDepartmentEmployees",
          error
        );
      } finally {
        this.loadingEmployees = false;
      }
    },
    async handleDeptRemove() {
      if (this.useEmployeeFilterApi) {
        return this.fetchFilteredEmployees();
      }
      this.fetchEmployees();
    },
    async resetFilters() {
      this.selectedDivision = null;
      this.selectedDept = null;
      this.selectedDesignation = null;
      this.selectedEmployee = this.multipleEmployees ? [] : null;
      this.localValues = { month: "", startDate: "", endDate: "" };
      if (this.useEmployeeFilterApi) {
        await this.fetchFilteredEmployees();
      } else {
        await this.fetchEmployees();
        if (this.showDept) {
          await this.fetchDepartments();
        }
      }
      this.$refs.leaveReportFilterForm?.resetForm();
      this.$emit("reset");
    },
    handleSubmit(values) {
      const payload = {
        ...values,
        from: this.localValues.startDate,
        to: this.localValues.endDate,
        month: this.localValues.month,
        startDate: this.localValues.startDate,
        endDate: this.localValues.endDate,
        employeeIds: this.selectedEmployee,
      };
      if (this.showDivision) {
        payload['division_id'] = this.selectedDivision;
      }
      if (this.showDept) {
        payload['department_id'] = this.selectedDept;
      }
      payload['designation_id'] = this.selectedDesignation;

      this.$emit("submit", payload);
    },

  },
  created() {
    if (this.useEmployeeFilterApi) {
      this.fetchFilteredEmployees();
    } else {
      this.fetchEmployees();
    }
    this.fetchDesignations();
    if (this.showDivision) {
      this.fetchDivisions();
    }
    if (this.showDept) {
      this.fetchDepartments();
    }
  },
  setup(props) {
    const schema = computed(() => {
      return Yup.object().shape({
        from: Yup.string().test(
          "date-range-or-month",
          "Select both From and To dates, or select a Month",
          function (value) {
            const { to, month } = this.parent;
            return Boolean(month || (value && to));
          },
        ),
        to: Yup.string().test(
          "complete-date-range",
          "Both From and To dates are required",
          function (value) {
            const { from, month } = this.parent;
            return Boolean(month || (from && value));
          },
        ),
        month: Yup.string().test(
          "exclusive-period-filter",
          "Use either Month or the From/To date range, not both",
          function (value) {
            const { from, to } = this.parent;
            return !(value && (from || to));
          },
        ),
        employee_personal_id: props.isEmployeeRequired
          ? Yup.mixed().nullable() // not required
          : Yup.mixed().required("Employee is required"),
      });
    });
    return {
      schema,
    };
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
